import React, { useEffect, useState } from 'react';
import DataTable from '../../components/DataTablePtBr';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import api from '../../services/api';
import { showError, showSuccess, showConfirmation } from '../../services/alert';

const NotaFiscalList = () => {
  const [notas, setNotas] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const fetchNotas = async () => {
    setLoading(true);
    try {
      const res = await api.get('/notafiscal');
      setNotas(res.data);
    } catch (err) {
      console.error('Erro ao buscar notas fiscais:', err);
      showError('Erro ao buscar notas fiscais.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotas();
  }, []);

  const handleValidar = async (id) => {
    try {
      const { data } = await api.post(`/notafiscal/${id}/validar`);
      showSuccess(`Nota validada: ${data.status}`);
      fetchNotas();
    } catch (err) {
      console.error('Erro ao validar nota fiscal:', err);
      const msg = err?.response?.data?.message || err?.response?.data || 'Falha ao validar nota fiscal.';
      showError(typeof msg === 'string' ? msg : 'Falha ao validar nota fiscal.');
    }
  };

  const handleDelete = async (id) => {
    const confirmou = await showConfirmation({
      title: 'Excluir nota fiscal?',
      text: 'Essa ação não pode ser desfeita.',
      confirmText: 'Sim, excluir',
      cancelText: 'Cancelar',
    });
    if (!confirmou) return;

    try {
      await api.delete(`/notafiscal/${id}`);
      showSuccess('Nota fiscal excluída com sucesso.');
      fetchNotas();
    } catch (err) {
      console.error('Erro ao excluir nota fiscal:', err);
      const msg = err?.response?.data?.message || err?.response?.data || 'Erro ao excluir nota fiscal.';
      showError(typeof msg === 'string' ? msg : 'Erro ao excluir nota fiscal.');
    }
  };

  const handleBaixarXml = async (id, chave) => {
    try {
      const response = await api.get(`/notafiscal/${id}/xml`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `NFe-${chave}.xml`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showError('XML não disponível.');
    }
  };

  const handleBaixarPdf = async (id, chave) => {
    try {
      const response = await api.get(`/notafiscal/${id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `NFe-${chave}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showError('PDF não disponível.');
    }
  };

  // ✅ Cancelar NF-e
  const handleCancelar = async (row) => {
    // Só permite cancelar se autorizada e tiver idNuvem
    const statusAutorizada = row.status === 2 || row.status === 'Autorizada';
    if (!statusAutorizada) {
      showError('Somente NF-e autorizada pode ser cancelada.');
      return;
    }

    if (!row.idNuvem) {
      showError('A nota não possui identificador na nuvem para cancelamento.');
      return;
    }

    // Pede justificativa (simples, sem modal)
    const justificativa = window.prompt('Informe a justificativa do cancelamento (mín. 15 caracteres):');
    if (justificativa === null) return; // cancelou
    if (!justificativa || justificativa.trim().length < 15) {
      showError('A justificativa deve ter no mínimo 15 caracteres.');
      return;
    }

    const confirmou = await showConfirmation({
      title: 'Cancelar NF-e?',
      text: 'Essa ação irá enviar o cancelamento para a SEFAZ.',
      confirmText: 'Sim, cancelar',
      cancelText: 'Voltar',
    });
    if (!confirmou) return;

    try {
      // O backend encaminha o cancelamento ao provedor ACBr.
      const res = await api.post(`/notafiscal/${row.id}/cancelar`, {
        justificativa: justificativa.trim(),
      });

      // backend pode retornar string pura
      const msg = res?.data?.message || res?.data || 'NF-e cancelada com sucesso.';
      showSuccess(typeof msg === 'string' ? msg : 'NF-e cancelada com sucesso.');
      fetchNotas();
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.message || err?.response?.data || 'Erro ao cancelar NF-e.';
      showError(typeof msg === 'string' ? msg : 'Erro ao cancelar NF-e.');
    }
  };

  const renderStatusBadge = (row) => {
    // normalize para texto
    const statusText =
      row.status === 2 ? 'Autorizada' :
      row.status === 3 ? 'Cancelada' :
      row.status;

    const badgeClass =
      row.status === 2 || row.status === 'Autorizada'
        ? 'bg-success'
        : row.status === 3 || row.status === 'Cancelada'
        ? 'bg-warning text-dark'
        : row.status === 'Rejeitada' || row.status === 4
        ? 'bg-danger'
        : 'bg-secondary';

    return <span className={`badge ${badgeClass}`}>{statusText}</span>;
  };

  const columns = [
    { name: '#', selector: row => row.id, width: '70px', sortable: true },
    { name: 'Número', selector: row => row.numero || '-', sortable: true },
    { name: 'Chave', selector: row => row.chaveAcesso || '-', sortable: true },

    // ✅ NOVO: IdNuvem
    {
      name: 'IdNuvem',
      selector: row => row.idNuvem || '-',
      sortable: true,
      wrap: true,
      width: '260px',
      omit: true 
    },

    {
      name: 'Status',
      selector: row => row.status,
      cell: row => renderStatusBadge(row),
      sortable: true,
      width: '130px',
    },

    { name: 'Valor Total', selector: row => `R$ ${Number(row.valorTotal || 0).toFixed(2)}`, sortable: true },

    {
      name: 'Data de emissão',
      selector: row => (row.dataEmissao ? new Date(row.dataEmissao).toLocaleDateString() : '-'),
      sortable: true,
    },

    {
      name: 'Ações',
      cell: row => {
        const statusAutorizada = row.status === 2 || row.status === 'Autorizada';
        const statusCancelada = row.status === 3 || row.status === 'Cancelada';

        return (
          <div className="d-flex gap-1">
            <button
              className="btn btn-outline-secondary btn-sm"
              onClick={() => handleBaixarXml(row.id, row.chaveAcesso)}
              title="Baixar XML"
              disabled={loading}
            >
              <i className="bi bi-file-earmark-code"></i>
            </button>

            <button
              className="btn btn-outline-dark btn-sm"
              onClick={() => handleBaixarPdf(row.id, row.chaveAcesso)}
              title="Baixar PDF"
              disabled={loading}
            >
              <i className="bi bi-filetype-pdf"></i>
            </button>

            {/* ✅ NOVO: Cancelar */}
            <button
              className="btn btn-outline-warning btn-sm"
              onClick={() => handleCancelar(row)}
              title="Cancelar NF-e"
              disabled={loading || !statusAutorizada || statusCancelada || !row.idNuvem}
            >
              <i className="bi bi-x-circle"></i>
            </button>

            <button
              className="btn btn-outline-danger btn-sm"
              onClick={() => handleDelete(row.id)}
              title="Excluir Nota"
              disabled={loading || statusAutorizada} // já segue sua regra (não excluir autorizada)
            >
              <i className="bi bi-trash"></i>
            </button>
          </div>
        );
      },
      ignoreRowClick: true,
      width: '310px',
    },
  ];

  return (
    <div className="container mt-4">
      <Header />
      <div className="card shadow-lg border-0 rounded-3">
        <div className="card-header bg-success text-white d-flex justify-content-between align-items-center rounded-top-3">
          <div className="d-flex align-items-center gap-3">
            <button
              className="btn btn-light btn-sm d-flex align-items-center gap-1"
              onClick={() => navigate('/menu')}
              disabled={loading}
            >
              <i className="bi bi-arrow-left text-success"></i>
              <span className="text-success fw-semibold">Voltar</span>
            </button>
            <div>
              <h5 className="mb-0">Notas Fiscais</h5>
              <small className="text-white-50">
                Gerencie as notas fiscais emitidas e validadas no sistema
              </small>
            </div>
          </div>
          <button
            className="btn btn-light text-success fw-bold"
            onClick={() => navigate('/notas-fiscais/nova')}
            disabled={loading}
          >
            <i className="bi bi-plus-circle"></i> Nova Nota Fiscal
          </button>
        </div>

        <div className="card-body">
          <DataTable
            columns={columns}
            data={notas}
            pagination
            responsive
            striped
            highlightOnHover
            progressPending={loading}
            noDataComponent={<div className="text-muted">Nenhuma nota fiscal encontrada.</div>}
          />
        </div>
      </div>
    </div>
  );
};

export default NotaFiscalList;
